import { useState, useEffect } from 'react';
import {
  Sparkles,
  FolderOpen,
  FileCode,
  FileJson,
  GitBranch,
  Search,
  Settings,
  Terminal,
  ChevronRight,
  ChevronDown,
  Check,
  Play,
  Bug,
  Puzzle
} from 'lucide-react';

// Realistic code that will be typed
const codeLines = [
  { text: "import { OpenAI } from 'openai';", delay: 0 },
  { text: "import { z } from 'zod';", delay: 50 },
  { text: "", delay: 30 },
  { text: "const client = new OpenAI({", delay: 60 },
  { text: "  apiKey: process.env.OPENAI_API_KEY,", delay: 40 },
  { text: "});", delay: 30 },
  { text: "", delay: 30 },
  { text: "// AI generates function based on comment", delay: 80, isComment: true },
  { text: "async function analyzeCode(code: string) {", delay: 60, isAI: true },
  { text: "  const response = await client.chat.completions.create({", delay: 40, isAI: true },
  { text: "    model: 'gpt-4',", delay: 30, isAI: true },
  { text: "    messages: [{ role: 'user', content: code }],", delay: 30, isAI: true },
  { text: "  });", delay: 20, isAI: true },
  { text: "  return response.choices[0].message.content;", delay: 40, isAI: true },
  { text: "}", delay: 20, isAI: true },
];

interface FileItem {
  name: string;
  isFolder: boolean;
  isOpen?: boolean;
  isActive?: boolean;
  children?: FileItem[];
}

const fileTree: FileItem[] = [
  { name: 'src', isFolder: true, isOpen: true, children: [
    { name: 'components', isFolder: true, isOpen: false },
    { name: 'services', isFolder: true, isOpen: true, children: [
      { name: 'ai.ts', isFolder: false, isActive: true },
      { name: 'api.ts', isFolder: false },
    ]},
    { name: 'App.tsx', isFolder: false },
    { name: 'index.ts', isFolder: false },
  ]},
  { name: 'package.json', isFolder: false },
  { name: 'tsconfig.json', isFolder: false },
];

const tabs = [
  { name: 'ai.ts', isActive: true },
  { name: 'App.tsx', isActive: false },
];

const aiMessages = [
  { type: 'user', text: 'Create a function to analyze code with GPT-4' },
  { type: 'ai', text: 'I\'ll create an async function that uses the OpenAI SDK to analyze code. Adding proper TypeScript types...' },
];

export function LiveCodeEditor() {
  const [visibleLines, setVisibleLines] = useState(0);
  const [typingIndex, setTypingIndex] = useState(0);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiTyping, setAiTyping] = useState(false);
  const [terminalOutput, setTerminalOutput] = useState<string[]>([]);

  // Reset and restart animation
  useEffect(() => {
    const totalLines = codeLines.length;

    if (visibleLines >= totalLines) {
      // Show terminal output after code is complete
      setTimeout(() => {
        setTerminalOutput(['$ npm run build', '✓ Compiled successfully in 1.2s']);
      }, 500);

      // Reset after delay
      setTimeout(() => {
        setVisibleLines(0);
        setTypingIndex(0);
        setShowAIPanel(false);
        setTerminalOutput([]);
      }, 4000);
      return;
    }

    const currentLine = codeLines[visibleLines];

    // Show AI panel when AI starts generating
    if (currentLine?.isAI && !showAIPanel) {
      setShowAIPanel(true);
      setAiTyping(true);
    }

    // Typing effect
    if (typingIndex < currentLine.text.length) {
      const timer = setTimeout(() => {
        setTypingIndex(prev => prev + 1);
      }, currentLine.isAI ? 15 : 25);
      return () => clearTimeout(timer);
    } else {
      // Move to next line
      const timer = setTimeout(() => {
        setVisibleLines(prev => prev + 1);
        setTypingIndex(0);
        if (codeLines[visibleLines]?.isAI) {
          setAiTyping(false);
        }
      }, currentLine.delay || 50);
      return () => clearTimeout(timer);
    }
  }, [visibleLines, typingIndex, showAIPanel]);

  const renderFileTree = (items: FileItem[], depth = 0) => (
    <div style={{ paddingLeft: depth * 12 }}>
      {items.map((item, idx) => (
        <div key={idx}>
          <div className={`flex items-center gap-1.5 py-0.5 px-1 rounded text-xs cursor-pointer hover:bg-white/5 ${item.isActive ? 'bg-accent/20 text-accent' : 'text-text-secondary'}`}>
            {item.isFolder ? (
              <>
                {item.isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <FolderOpen size={12} className="text-yellow-500" />
              </>
            ) : (
              <>
                <span className="w-3" />
                <FileCode size={12} className="text-blue-400" />
              </>
            )}
            <span>{item.name}</span>
          </div>
          {item.isFolder && item.isOpen && item.children && renderFileTree(item.children, depth + 1)}
        </div>
      ))}
    </div>
  );

  return (
    <div className="rounded-xl overflow-hidden border border-[#2d2d3d] shadow-2xl bg-[#1e1e2e]">
      {/* Title Bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-[#181825] border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <span className="text-xs text-text-tertiary ml-2">Ochtopus — ai-project</span>
        </div>
        <div className="flex items-center gap-2 text-text-tertiary">
          <div className="flex items-center gap-1 text-xs bg-accent/20 text-accent px-2 py-0.5 rounded">
            <Sparkles size={10} />
            AI Ready
          </div>
        </div>
      </div>

      <div className="flex h-[320px]">
        {/* Activity Bar */}
        <div className="w-10 bg-[#181825] flex flex-col items-center py-2 gap-3 border-r border-white/5">
          <FileCode size={18} className="text-accent" />
          <Search size={18} className="text-text-tertiary hover:text-text-secondary cursor-pointer" />
          <GitBranch size={18} className="text-text-tertiary hover:text-text-secondary cursor-pointer" />
          <Bug size={18} className="text-text-tertiary hover:text-text-secondary cursor-pointer" />
          <Puzzle size={18} className="text-text-tertiary hover:text-text-secondary cursor-pointer" />
          <div className="flex-1" />
          <Sparkles size={18} className="text-accent animate-pulse" />
          <Settings size={18} className="text-text-tertiary hover:text-text-secondary cursor-pointer" />
        </div>

        {/* Sidebar - File Explorer */}
        <div className="w-40 bg-[#1e1e2e] border-r border-white/5 overflow-hidden">
          <div className="text-[10px] uppercase tracking-wider text-text-tertiary px-3 py-2">Explorer</div>
          {renderFileTree(fileTree)}
        </div>

        {/* Main Editor Area */}
        <div className="flex-1 flex flex-col">
          {/* Tabs */}
          <div className="flex bg-[#181825] border-b border-white/5">
            {tabs.map((tab, idx) => (
              <div
                key={idx}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border-r border-white/5 ${
                  tab.isActive ? 'bg-[#1e1e2e] text-text-primary' : 'text-text-tertiary'
                }`}
              >
                <FileCode size={12} className="text-blue-400" />
                {tab.name}
              </div>
            ))}
          </div>

          {/* Code Editor */}
          <div className="flex-1 overflow-hidden">
            <div className="p-3 font-mono text-xs leading-5 h-full overflow-auto">
              {codeLines.slice(0, visibleLines + 1).map((line, idx) => {
                const isCurrentLine = idx === visibleLines;
                const displayText = isCurrentLine ? line.text.slice(0, typingIndex) : line.text;

                return (
                  <div key={idx} className="flex">
                    <span className="w-6 text-text-tertiary/40 select-none text-right pr-3">
                      {idx + 1}
                    </span>
                    <span className={`flex-1 ${
                      line.isComment ? 'text-green-500' :
                      line.isAI ? 'text-purple-400' :
                      'text-text-primary'
                    }`}>
                      {displayText.split(/(\s+|[{}();,]|'[^']*')/).map((part, i) => {
                        if (part.match(/^(import|export|from|const|async|function|await|return|new)$/)) {
                          return <span key={i} className="text-purple-400">{part}</span>;
                        }
                        if (part.match(/^'.*'$/)) {
                          return <span key={i} className="text-green-400">{part}</span>;
                        }
                        if (part.match(/^(OpenAI|z|client|response|process)$/)) {
                          return <span key={i} className="text-blue-400">{part}</span>;
                        }
                        return <span key={i}>{part}</span>;
                      })}
                      {isCurrentLine && (
                        <span className="inline-block w-1.5 h-4 bg-accent ml-0.5 animate-pulse" />
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Terminal */}
          {terminalOutput.length > 0 && (
            <div className="h-12 bg-[#181825] border-t border-white/5 p-2 font-mono text-xs">
              <div className="flex items-center gap-2 text-text-tertiary mb-1">
                <Terminal size={10} />
                Terminal
              </div>
              {terminalOutput.map((line, idx) => (
                <div key={idx} className={line.startsWith('✓') ? 'text-green-400' : 'text-text-secondary'}>
                  {line}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AI Panel */}
        {showAIPanel && (
          <div className="w-48 bg-[#181825] border-l border-white/5 flex flex-col animate-fade-in">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
              <Sparkles size={12} className="text-accent" />
              <span className="text-xs font-medium text-accent">AI Agent</span>
            </div>
            <div className="flex-1 p-2 space-y-2 overflow-auto">
              {aiMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`text-[10px] p-2 rounded ${
                    msg.type === 'user'
                      ? 'bg-white/5 text-text-secondary'
                      : 'bg-accent/10 text-accent border border-accent/20'
                  }`}
                >
                  {msg.text}
                </div>
              ))}
              {aiTyping && (
                <div className="flex items-center gap-1 text-accent text-[10px]">
                  <span className="animate-pulse">●</span>
                  <span className="animate-pulse delay-100">●</span>
                  <span className="animate-pulse delay-200">●</span>
                  <span className="ml-1">Writing code...</span>
                </div>
              )}
            </div>
            <div className="p-2 border-t border-white/5">
              <div className="flex items-center gap-1 text-[10px] text-green-400">
                <Check size={10} />
                6 lines generated
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="flex items-center justify-between px-3 py-1 bg-[#8B5CF6] text-white text-[10px] rounded-b-xl">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <GitBranch size={10} />
            main
          </span>
          <span>TypeScript</span>
        </div>
        <div className="flex items-center gap-3">
          <span>UTF-8</span>
          <span>Ln {visibleLines + 1}, Col {typingIndex + 1}</span>
          <span className="flex items-center gap-1">
            <Sparkles size={10} />
            GPT-4
          </span>
        </div>
      </div>
    </div>
  );
}
